import React, { useState, useEffect } from 'react';
import {
    Box,
    Grid,
    Card,
    CardContent,
    Typography,
    TextField,
    InputAdornment,
    Stack,
    Alert,
    CircularProgress,
    Chip,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Divider,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button
} from '@mui/material';
import {
    Search,
    Build,
    ExpandMore,
    CheckCircle,
    Warning,
    Info,
    Visibility,
    Security,
    Assignment,
    Engineering
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import apiClient from '../../api';

const EquipmentList = ({ searchTerm, filterCategory, metadata }) => {
    const { user } = useAuth();
    const [equipment, setEquipment] = useState([]);
    const [loading, setLoading] = useState(true);
    const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm || '');
    const [selectedEquipment, setSelectedEquipment] = useState(null);
    const [detailDialogOpen, setDetailDialogOpen] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        fetchEquipment();
    }, [searchTerm, filterCategory]);

    useEffect(() => {
        setLocalSearchTerm(searchTerm || '');
    }, [searchTerm]);

    const fetchEquipment = async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams({
                type: 'equipment',
                ...(searchTerm && { search: searchTerm }),
                ...(filterCategory && { category: filterCategory })
            });

            const response = await apiClient.get(`/safety/content?${params}`);
            setEquipment(response.data.data || []);
        } catch (error) {
            console.error('Error fetching equipment:', error);
            setError('Failed to load equipment list');
        } finally {
            setLoading(false);
        }
    };

    const handleLocalSearch = (searchValue) => {
        setLocalSearchTerm(searchValue);
        // Filter equipment locally for instant search
        if (searchValue.trim() === '') {
            fetchEquipment();
        } else {
            const filtered = equipment.filter(item =>
                item.title.toLowerCase().includes(searchValue.toLowerCase()) ||
                item.description.toLowerCase().includes(searchValue.toLowerCase()) ||
                item.tags.some(tag => tag.toLowerCase().includes(searchValue.toLowerCase())) ||
                item.equipmentTypes.some(type => type.toLowerCase().includes(searchValue.toLowerCase()))
            );
            setEquipment(filtered);
        }
    };

    const handleEquipmentClick = (equipmentItem) => {
        setSelectedEquipment(equipmentItem);
        setDetailDialogOpen(true);
    };

    const getSafetyRating = (tags) => {
        if (tags.includes('high_risk') || tags.includes('dangerous')) {
            return { label: 'High Risk', color: 'error', icon: <Warning /> };
        } else if (tags.includes('medium_risk') || tags.includes('caution')) {
            return { label: 'Medium Risk', color: 'warning', icon: <Warning /> };
        } else if (tags.includes('low_risk') || tags.includes('safe')) {
            return { label: 'Low Risk', color: 'success', icon: <CheckCircle /> };
        }
        return { label: 'Standard', color: 'default', icon: <Info /> };
    };

    const getCategoryIcon = (category) => {
        switch (category) {
            case 'equipment_operation':
                return <Engineering color="primary" />;
            case 'load_securing':
                return <Security color="primary" />;
            case 'moffett_training':
                return <Build color="primary" />;
            case 'truss_trailer':
                return <Assignment color="primary" />;
            default:
                return <Build color="primary" />;
        }
    };

    const groupedEquipment = equipment.reduce((groups, item) => {
        const category = item.category || 'general';
        if (!groups[category]) {
            groups[category] = [];
        }
        groups[category].push(item);
        return groups;
    }, {});

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ mb: 2 }}>
                {error}
            </Alert>
        );
    }

    return (
        <Box>
            <Stack direction="row" alignItems="center" spacing={2} mb={3}>
                <Build color="primary" fontSize="large" />
                <Typography variant="h5">
                    Equipment Reference ({equipment.length})
                </Typography>
            </Stack>

            {/* Local Search */}
            <TextField
                fullWidth
                placeholder="Search equipment by name, description, or specifications..."
                value={localSearchTerm}
                onChange={(e) => handleLocalSearch(e.target.value)}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <Search />
                        </InputAdornment>
                    ),
                }}
                sx={{ mb: 3 }}
            />

            <Alert severity="info" sx={{ mb: 3 }}>
                Comprehensive equipment reference with safety specifications, operating procedures, 
                and maintenance guidelines for all operational equipment.
            </Alert>

            {equipment.length === 0 ? (
                <Alert severity="info">
                    No equipment found. Try adjusting your search criteria.
                </Alert>
            ) : (
                <Box>
                    {Object.entries(groupedEquipment).map(([category, items]) => (
                        <Accordion key={category} defaultExpanded>
                            <AccordionSummary
                                expandIcon={<ExpandMore />}
                                sx={{ bgcolor: 'grey.100' }}
                            >
                                <Stack direction="row" alignItems="center" spacing={2}>
                                    {getCategoryIcon(category)}
                                    <Typography variant="h6">
                                        {category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} ({items.length})
                                    </Typography>
                                </Stack>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Grid container spacing={2}>
                                    {items.map((item) => (
                                        <Grid item xs={12} md={6} lg={4} key={item._id}>
                                            <Card 
                                                sx={{ 
                                                    height: '100%',
                                                    transition: 'transform 0.2s, box-shadow 0.2s',
                                                    cursor: 'pointer',
                                                    '&:hover': { 
                                                        transform: 'translateY(-2px)',
                                                        boxShadow: '0 4px 20px rgba(0,0,0,0.12)'
                                                    }
                                                }}
                                                onClick={() => handleEquipmentClick(item)}
                                            >
                                                <CardContent>
                                                    <Stack spacing={2}>
                                                        {/* Header */}
                                                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                                            <Typography variant="h6" sx={{ 
                                                                fontWeight: 'bold',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                display: '-webkit-box',
                                                                WebkitLineClamp: 2,
                                                                WebkitBoxOrient: 'vertical'
                                                            }}>
                                                                {item.title}
                                                            </Typography>
                                                            <IconButton size="small">
                                                                <Visibility />
                                                            </IconButton>
                                                        </Stack>

                                                        {/* Safety Rating */}
                                                        {item.tags && item.tags.length > 0 && (
                                                            <Chip
                                                                {...getSafetyRating(item.tags)}
                                                                size="small"
                                                                sx={{ alignSelf: 'flex-start' }}
                                                            />
                                                        )}

                                                        {/* Description */}
                                                        <Typography 
                                                            variant="body2" 
                                                            color="text.secondary"
                                                            sx={{ 
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                display: '-webkit-box',
                                                                WebkitLineClamp: 3,
                                                                WebkitBoxOrient: 'vertical'
                                                            }}
                                                        >
                                                            {item.description}
                                                        </Typography>

                                                        {/* Equipment Types */}
                                                        {item.equipmentTypes && item.equipmentTypes.length > 0 && (
                                                            <Stack direction="row" spacing={1} flexWrap="wrap">
                                                                {item.equipmentTypes.slice(0, 2).map((type, index) => (
                                                                    <Chip 
                                                                        key={index} 
                                                                        label={type} 
                                                                        size="small" 
                                                                        variant="outlined"
                                                                        color="primary"
                                                                    />
                                                                ))}
                                                                {item.equipmentTypes.length > 2 && (
                                                                    <Chip 
                                                                        label={`+${item.equipmentTypes.length - 2}`} 
                                                                        size="small" 
                                                                        variant="outlined" 
                                                                        color="secondary"
                                                                    />
                                                                )}
                                                            </Stack>
                                                        )}

                                                        {/* Priority Indicator */}
                                                        {item.priority === 'high' || item.priority === 'critical' && (
                                                            <Alert severity={item.priority === 'critical' ? 'error' : 'warning'} sx={{ py: 0 }}>
                                                                <Typography variant="caption">
                                                                    {item.priority.toUpperCase()} Priority Equipment
                                                                </Typography>
                                                            </Alert>
                                                        )}
                                                    </Stack>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    ))}
                                </Grid>
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </Box>
            )}

            {/* Equipment Detail Dialog */}
            <Dialog
                open={detailDialogOpen}
                onClose={() => setDetailDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">
                            {selectedEquipment?.title}
                        </Typography>
                        <Stack direction="row" spacing={1}>
                            {selectedEquipment?.tags && selectedEquipment.tags.length > 0 && (
                                <Chip
                                    {...getSafetyRating(selectedEquipment.tags)}
                                    size="small"
                                />
                            )}
                        </Stack>
                    </Stack>
                </DialogTitle>
                
                <DialogContent>
                    {selectedEquipment && (
                        <Stack spacing={3}>
                            {/* Description */}
                            <Box>
                                <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                                    Description
                                </Typography>
                                <Typography variant="body1">
                                    {selectedEquipment.description}
                                </Typography>
                            </Box>

                            <Divider />

                            {/* Equipment Types & Specifications */}
                            {selectedEquipment.equipmentTypes && selectedEquipment.equipmentTypes.length > 0 && (
                                <Box>
                                    <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                                        Equipment Types
                                    </Typography>
                                    <Stack direction="row" spacing={1} flexWrap="wrap">
                                        {selectedEquipment.equipmentTypes.map((type, index) => (
                                            <Chip 
                                                key={index} 
                                                label={type} 
                                                variant="outlined"
                                                color="primary"
                                            />
                                        ))}
                                    </Stack>
                                </Box>
                            )}

                            {/* Safety Guidelines */}
                            {selectedEquipment.tags && selectedEquipment.tags.length > 0 && (
                                <Box>
                                    <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                                        Safety Guidelines & Tags
                                    </Typography>
                                    <List dense>
                                        {selectedEquipment.tags.map((tag, index) => (
                                            <ListItem key={index}>
                                                <ListItemIcon>
                                                    <CheckCircle color="primary" fontSize="small" />
                                                </ListItemIcon>
                                                <ListItemText 
                                                    primary={tag.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                </Box>
                            )}

                            {/* Additional Information */}
                            <Box>
                                <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                                    Additional Information
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <Typography variant="body2" color="text.secondary">
                                            Category
                                        </Typography>
                                        <Typography variant="body1">
                                            {selectedEquipment.category?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="body2" color="text.secondary">
                                            Priority
                                        </Typography>
                                        <Typography variant="body1">
                                            {selectedEquipment.priority?.replace(/\b\w/g, l => l.toUpperCase())}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="body2" color="text.secondary">
                                            Required Training
                                        </Typography>
                                        <Typography variant="body1">
                                            {selectedEquipment.isRequired ? 'Yes' : 'No'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="body2" color="text.secondary">
                                            Last Updated
                                        </Typography>
                                        <Typography variant="body1">
                                            {new Date(selectedEquipment.updatedAt).toLocaleDateString()}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Box>

                            {/* Related Documentation */}
                            {selectedEquipment.documentUrl && (
                                <Box>
                                    <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                                        Documentation
                                    </Typography>
                                    <Button
                                        variant="outlined"
                                        startIcon={<Assignment />}
                                        onClick={() => window.open(selectedEquipment.documentUrl, '_blank')}
                                    >
                                        View Documentation
                                    </Button>
                                </Box>
                            )}
                        </Stack>
                    )}
                </DialogContent>
                
                <DialogActions>
                    <Button onClick={() => setDetailDialogOpen(false)}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default EquipmentList; 